import { GAME_HAND, GAME_HAND_PHASE } from 'constants/gameConstants';

/**
 * EN-10985: the between-hand pace, for the QA Performance HUD.
 *
 * `HandPace` is what the hand processors attach to each table update they
 * publish (handPaceForDisplay in shared/config/handPace.js): the pace that step
 * ran with, and whether it is the old (untrimmed) pace. cash-game-broadcast
 * passes it through. Display only.
 *
 * The gap tracker measures what this client saw: from the frame that ends the
 * betting (find/pay winners) to the next hand's DEAL_CARDS frame (hand 4, the
 * face-down deal; faces arrive at hand 5). It is a client-observed proxy for
 * before/after comparisons of the pace settings, not the ticket's engine-side
 * "pot award to first action" measurement.
 */
export type HandPace = {
  winnerSecs: number;
  foldWinnerSecs: number;
  holdSecs: number;
  skipIdleStep: boolean;
  chainSteps: boolean;
  chainGapMs: number;
  collectHoldMs: number;
  isDefault: boolean;
};

export type HandGap = {
  ms: number;
  /** Everyone else folded: no cards to show, so the gap is shorter. */
  foldWin: boolean;
};

export type HandFrame = {
  gameID: number | string;
  hand: number | string;
  /** The hand counter: the engine adds one at each dealer button. */
  gameNo?: number | string;
  languageKey?: string;
};

const FOLD_WIN_MESSAGE = 'GAME_MSG_ALLFOLD';

const handNumber = (hand: number | string): number | null => {
  const parsed = parseInt(String(hand), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/** A betting street: the last action of a hand happens in one of these. */
const isBetting = (hand: number | null) =>
  hand !== null &&
  hand >= GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND &&
  hand < GAME_HAND_PHASE.FIND_WINNERS;

/**
 * The frame right after the last action: find winners (showdown) or pay winners
 * (everyone else folded). Anything later means frames were missed in between.
 */
const isEndOfBetting = (hand: number | null) =>
  hand === GAME_HAND.FIND_WINNERS || hand === GAME_HAND.PAY_WINNERS;

/** The hand is being settled (showdown, winners) after the last action. */
const isSettling = (hand: number | null) => hand !== null && hand >= GAME_HAND_PHASE.FIND_WINNERS;

export type HandGapTracker = {
  /** Feed every table frame in order; returns a gap when one has just ended. */
  observe: (frame: HandFrame, now: number) => HandGap | null;
  /** Forget the hand in progress: frames were not being received (tab hidden, connection lost). */
  invalidate: () => void;
};

/**
 * Times one table's gaps: from the frame that ends the betting (a betting frame
 * followed straight by find/pay winners) to the next DEAL_CARDS frame.
 *
 * A wrong gap is worse than none, because it reads as a pace change that never
 * happened, so a gap is only kept when both of its ends were seen:
 * - the client only sees the newest frame per animation frame, and none while the
 *   tab is hidden or the socket reconnects. Betting followed by anything but
 *   find/pay winners (e.g. a return to the tab at hand 2) starts nothing;
 * - a betting frame while a gap is open means its DEAL_CARDS frame was missed and
 *   the "gap" would run through a whole hand, so it is dropped;
 * - so is a gap whose hand counter (gameNo) moved more than once: a gap spans
 *   exactly one dealer button, and a reconnect can skip every betting frame of
 *   the hand in between;
 * - a different table, or invalidate(), drops it too.
 */
export const createHandGapTracker = (): HandGapTracker => {
  let gameID: string | null = null;
  let previousHand: number | null = null;
  let previousGameNo: number | null = null;
  let startedAt: number | null = null;
  let startGameNo: number | null = null;
  let foldWin = false;

  /** More than one dealer button since the gap began; unknown counters pass. */
  const spansSeveralHands = (gameNo: number | null) =>
    gameNo !== null && startGameNo !== null && gameNo - startGameNo > 1;

  /** Both frames belong to one hand; unknown counters pass. */
  const sameHand = (gameNo: number | null) =>
    gameNo === null || previousGameNo === null || gameNo === previousGameNo;

  const observe = (frame: HandFrame, now: number): HandGap | null => {
    const id = String(frame.gameID);
    const hand = handNumber(frame.hand);
    const gameNo = frame.gameNo === undefined ? null : handNumber(frame.gameNo);

    if (id !== gameID) {
      gameID = id;
      previousHand = hand;
      previousGameNo = gameNo;
      startedAt = null;
      foldWin = false;
      return null;
    }

    let finished: HandGap | null = null;

    if (startedAt === null) {
      // Betting then find/pay winners OF THE SAME HAND: after a missed deal and
      // betting, the next hand's showdown follows a betting frame of the last one.
      if (isBetting(previousHand) && isEndOfBetting(hand) && sameHand(gameNo)) {
        startedAt = now;
        startGameNo = gameNo;
        foldWin = false;
      }
    } else if (spansSeveralHands(gameNo) || isBetting(hand)) {
      startedAt = null;
    } else if (hand === GAME_HAND_PHASE.DEAL_CARDS) {
      finished = { ms: now - startedAt, foldWin };
      startedAt = null;
    }

    if (startedAt !== null && isSettling(hand) && frame.languageKey === FOLD_WIN_MESSAGE) {
      foldWin = true;
    }

    previousHand = hand;
    previousGameNo = gameNo;
    return finished;
  };

  return {
    observe,
    /**
     * Forget the hand in progress: the open gap AND the frame before it. Keeping
     * the previous frame let a betting frame seen before a hidden tab or a lost
     * connection pair with the first frame after it, starting a gap at the resume
     * (too short) instead of dropping it.
     */
    invalidate: () => {
      startedAt = null;
      startGameNo = null;
      foldWin = false;
      previousHand = null;
      previousGameNo = null;
    }
  };
};

/** "TRIMMED" / "default" headline for the HUD; null while no pace has arrived. */
export const handPaceLabel = (pace: HandPace | undefined): string | null => {
  if (!pace) return null;
  return pace.isDefault ? 'default' : 'TRIMMED';
};

/**
 * The values behind the label, as two short lines that fit the HUD's width:
 * ["win 2s fold 2s hold 1s skip", "chain on 250/1100ms"].
 */
export const handPaceDetail = (pace: HandPace): [string, string] => [
  [
    `win ${pace.winnerSecs}s`,
    `fold ${pace.foldWinnerSecs}s`,
    `hold ${pace.holdSecs}s`,
    pace.skipIdleStep ? 'skip' : null
  ]
    .filter(Boolean)
    .join(' '),
  pace.chainSteps ? `chain on ${pace.chainGapMs}/${pace.collectHoldMs}ms` : 'chain off'
];

/** "10.9s", or "10.9s fold" for a fold-win, which has no cards to show. */
export const formatHandGap = (gap: HandGap): string =>
  `${(gap.ms / 1000).toFixed(1)}s${gap.foldWin ? ' fold' : ''}`;
